import React, { useState, useEffect } from "react";
import { db, storage } from "../../utils/firebase";
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Navbar from "../navbar/Navbar";
import Footer from "../footer/Footer";
import "./AdminUsers.css"; // Custom styles
import { Link, useLocation } from 'react-router-dom';

const AnnouncementAdmin = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    description: "",
    from: "",
  });
  const location = useLocation();
  const [background, setBackground] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [images, setImages] = useState([]);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState(null); // Track the editing announcement

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "announcements"), (snapshot) => {
      const fetchedAnnouncements = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAnnouncements(fetchedAnnouncements);
    });
    return () => unsubscribe();
  }, []);

  // Open/close modal
  const toggleModal = () => {
    setIsModalOpen((prevState) => !prevState);
  };

  // Handle form inputs for new announcement
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAnnouncement({ ...newAnnouncement, [name]: value });
  };

  // Handle form inputs for editing announcements
  const handleEditInputChange = (e, id) => {
    const { name, value } = e.target;
    setAnnouncements((prevAnnouncements) =>
      prevAnnouncements.map((announcement) =>
        announcement.id === id
          ? { ...announcement, [name]: value }
          : announcement
      )
    );
  };

  // Handle image uploads for new announcements
  const handleFileUpload = (file, path) => {
    const fileRef = ref(storage, path);
    return uploadBytes(fileRef, file).then(() => getDownloadURL(fileRef));
  };

  // Handle multiple image uploads for new announcements
  const handleMultipleImagesUpload = (files, path) => {
    return Promise.all(
      Array.from(files).map((image, index) =>
        handleFileUpload(image, `${path}/${Date.now()}_${index}_${image.name}`)
      )
    );
  };

  // Create new announcement
  const createAnnouncement = async (e) => {
    e.preventDefault();

    try {
      const profileImageUrl = profileImage
        ? await handleFileUpload(profileImage, `profile_images/${newAnnouncement.from}/profile_image.jpg`)
        : "";
      const backgroundUrl = background
        ? await handleFileUpload(background, `announcements/background/${background.name}`)
        : "";
      const uploadedImages = await handleMultipleImagesUpload(images, "announcements/images");

      // Add new announcement to Firestore
      await addDoc(collection(db, "announcements"), {
        ...newAnnouncement,
        profile_image: profileImageUrl,
        background: backgroundUrl,
        images: uploadedImages,
      });

      // Reset form
      setNewAnnouncement({ title: "", description: "", from: "" });
      setBackground(null);
      setProfileImage(null);
      setImages([]);

      // Close modal
      toggleModal();
    } catch (error) {
      console.error("Error creating announcement:", error);
    }
  };

  // Edit announcement
  const editAnnouncement = async (announcementId) => {
    setEditingAnnouncementId(announcementId);
  };

  const saveEditAnnouncement = async (id) => {
    const updatedAnnouncement = announcements.find(
      (announcement) => announcement.id === id
    );

    // Update the announcement in Firestore
    try {
      await updateDoc(doc(db, "announcements", id), {
        title: updatedAnnouncement.title,
        description: updatedAnnouncement.description,
        from: updatedAnnouncement.from,
        // Upload images if updated
        profile_image: updatedAnnouncement.profile_image,
        background: updatedAnnouncement.background,
        images: updatedAnnouncement.images,
      });
      setEditingAnnouncementId(null);
    } catch (error) {
      console.error("Error saving announcement:", error);
    }
  };

  // Move announcement to archive and delete it
  const moveToArchive = async (id) => {
    try {
      const announcementToArchive = announcements.find(
        (announcement) => announcement.id === id
      );

      // Add to the 'announcements_archive' collection
      await addDoc(collection(db, "announcements_archive"), {
        ...announcementToArchive,
      });

      // Delete the announcement from 'announcements'
      await deleteDoc(doc(db, "announcements", id));
    } catch (error) {
      console.error("Error archiving and deleting announcement:", error);
    }
  };

  return (
    <div className="admin-page">
      <Navbar />
      <div className="admin-container">
        <div className="admin-sidebar">
           <h2>Admin Panel</h2>
           <Link to="/adminusers" className={`sidebar-button ${location.pathname === '/adminusers' ? 'active' : ''}`}>Users</Link>
           <Link to="/adminposts" className={`sidebar-button ${location.pathname === '/adminposts' ? 'active' : ''}`}>User Posts</Link>
           <Link to="/adminfeedback" className={`sidebar-button ${location.pathname === '/adminfeedback' ? 'active' : ''}`}>User Feedback</Link>
           <Link to="/announcementadmin" className={`sidebar-button ${location.pathname === '/announcementadmin' ? 'active' : ''}`}>Announcements</Link>
           <Link to="/exploreaboutadmin" className={`sidebar-button ${location.pathname === '/exploreaboutadmin' ? 'active' : ''}`}>Explore about Bacoor</Link>
 
           <h2>Places to Visit</h2>
           <Link to="/bridgesadmin" className={`sidebar-button ${location.pathname === '/bridgesadmin' ? 'active' : ''}`}>Bridges & Dams</Link>
           <Link to="/monumentsadmin" className={`sidebar-button ${location.pathname === '/monumentsadmin' ? 'active' : ''}`}>Monuments & Statues</Link>
           <Link to="/houseadmin" className={`sidebar-button ${location.pathname === '/houseadmin' ? 'active' : ''}`}>Ancestral Houses</Link>
           <Link to="/churchesadmin" className={`sidebar-button ${location.pathname === '/churchesadmin' ? 'active' : ''}`}>Churches & Religious Sites</Link>
 
           <h2>Things to Do</h2>
           <Link to="/recreationaladmin" className={`sidebar-button ${location.pathname === '/recreationaladmin' ? 'active' : ''}`}>Recreational Activities</Link>
           <Link to="/diningadmin" className={`sidebar-button ${location.pathname === '/diningadmin' ? 'active' : ''}`}>Dining and Culinary</Link>
           <Link to="/shoppingadmin" className={`sidebar-button ${location.pathname === '/shoppingadmin' ? 'active' : ''}`}>Shopping and Market</Link>
 
           <h2>Archives</h2>
           <Link to="/userarchive" className={`sidebar-button ${location.pathname === '/userarchive' ? 'active' : ''}`}>Users</Link>
           <Link to="/postsarchive" className={`sidebar-button ${location.pathname === '/postsarchive' ? 'active' : ''}`}>Posts</Link>
           <Link to="/feedbackarchive" className={`sidebar-button ${location.pathname === '/feedbackarchive' ? 'active' : ''}`}>Feedback</Link>
           <Link to="/announcementarchive" className={`sidebar-button ${location.pathname === '/announcementarchive' ? 'active' : ''}`}>Announcements</Link>
         </div>
        <div className="admin-content">
          <div className="table-container">
            <h2>
              Announcements Management
              <button className="create-announcement-btn" onClick={toggleModal}>
              Create Announcement
            </button>
            </h2>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>From</th>
                  <th>Background</th>
                  <th>Images</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr key={announcement.id}>
                    <td>
                      {editingAnnouncementId === announcement.id ? (
                        <input
                          type="text"
                          name="title"
                          value={announcement.title}
                          onChange={(e) => handleEditInputChange(e, announcement.id)}
                        />
                      ) : (
                        announcement.title
                      )}
                    </td>
                    <td>
                      {editingAnnouncementId === announcement.id ? (
                        <input
                          type="text"
                          name="description"
                          value={announcement.description}
                          onChange={(e) => handleEditInputChange(e, announcement.id)}
                        />
                      ) : (
                        announcement.description
                      )}
                    </td>
                    <td>
                      {editingAnnouncementId === announcement.id ? (
                        <input
                          type="text"
                          name="from"
                          value={announcement.from}
                          onChange={(e) => handleEditInputChange(e, announcement.id)}
                        />
                      ) : (
                        announcement.from
                      )}
                    </td>
                    <td>
                      {editingAnnouncementId === announcement.id ? (
                        <input
                          type="file"
                          onChange={(e) => setBackground(e.target.files[0])}
                          accept="image/*"
                        />
                      ) : (
                        <img
                          src={announcement.background}
                          alt="Background"
                          style={{ width: "100px", height: "auto" }}
                        />
                      )}
                    </td>
                    <td>
                      {editingAnnouncementId === announcement.id ? (
                        <input
                          type="file"
                          onChange={(e) => setImages(e.target.files)}
                          multiple
                          accept="image/*"
                        />
                      ) : (
                        announcement.images.map((image, index) => (
                          <img key={index} src={image} alt={`Image ${index}`} style={{ width: "100px", height: "auto" }} />
                        ))
                      )}
                    </td>
                    <td>
                {editingAnnouncementId === announcement.id ? (
                  <>
                    <button
                      className="save-btn btn btn-success"
                      onClick={() => saveEditAnnouncement(announcement.id)}
                    >
                      <i className="bi bi-check"></i>
                    </button>
                    <button
                      className="cancel-btn btn btn-secondary"
                      onClick={() => setEditingAnnouncementId(null)}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="edit-btn btn btn-primary"
                      onClick={() => editAnnouncement(announcement.id)}
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      className="delete-btn btn btn-danger"
                      onClick={() => moveToArchive(announcement.id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </>
                )}
              </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2>Create New Announcement</h2>
            <form onSubmit={createAnnouncement}>
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={newAnnouncement.title}
                onChange={handleInputChange}
              />
              <textarea
                name="description"
                placeholder="Description"
                value={newAnnouncement.description}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="from"
                placeholder="From"
                value={newAnnouncement.from}
                onChange={handleInputChange}
              />
              <label>Background Image</label>
              <input
                type="file"
                onChange={(e) => setBackground(e.target.files[0])}
                accept="image/*"
              />
              <label>Profile Image</label>
              <input
                type="file"
                onChange={(e) => setProfileImage(e.target.files[0])}
                accept="image/*"
              />
              <label>Announcement Images</label>
              <input
                type="file"
                onChange={(e) => setImages(e.target.files)}
                multiple
                accept="image/*"
              />
              <button class="save-btn" type="submit">Create Announcement</button>
              <button class="cancel-btn" onClick={toggleModal}>Close</button>
            </form>
            
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AnnouncementAdmin;
